// main.js
import { createApp } from "vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import App from "./App.vue";
import router from "./router";
import i18n from "./i18n";
import { initTranslator } from "./config";

import setupInterceptors from "./services/setupInterceptors";
import "./plugins/ui-components";

import vuetify from "./plugins/vuetify";
import { loadFonts } from "./plugins/webfontloader";
import { createToastInterface } from "vue-toastification";
import { VueFlow } from "@vue-flow/core";
import Toast from "vue-toastification";
import { useMainStore } from "./stores/store";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

import "./assets/scss/styles.scss";
import "@vue-flow/core/dist/style.css";
import "@vue-flow/core/dist/theme-default.css";
import "vue-toastification/dist/index.css";
import { VueDraggableNext } from "vue-draggable-next";

// Create the Vue app instance
const app = createApp(App);

// Create Pinia instance
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

// Configure Toast options (optional)
const toastOptions = {
  position: "bottom-center",
  timeout: 2000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  transition: "my-custom-fade",
  draggable: true,
  icon: false,
  draggablePercent: 0.6,
  maxToasts: 3,
  newestOnTop: true,
  hideProgressBar: true
};

// Create the toast instance and provide it to the app
const toast = createToastInterface(toastOptions);

app.provide("toast", toast);
app.config.globalProperties.$toast = toast;
app.config.globalProperties.$t = i18n.global.t;

app.use(i18n);
initTranslator(i18n.global.t);

// Setup interceptors for Axios
setupInterceptors();
loadFonts();

// Register global components (make sure to register before mounting)
import HomeSidebar from "./components/home/home-sidebar";
import AdminSidebar from "./components/admin/admin-sidebar";
import WorkspaceSettingsSidebar from "./components/settings/workspace-settings-sidebar";

import LeftDashboardSidebar from "./components/dashboards/left-sidebar";
import DashTopbar from "./components/dashboards/dashboard-topbar";
import Assignees from "./components/dashboards/datafields/assignees";
import Userstagmenu from "./components/dashboards/datafields/userstagmenu";
import Timetrackingmenu from "./components/dashboards/datafields/timetrackingmenu";
import Statustag from "./components/dashboards/datafields/statustag";
import statustagmenu from "./components/dashboards/datafields/statustagmenu.vue";
import Priotag from "./components/dashboards/datafields/priotag.vue";
import Priotagmenu from "./components/dashboards/datafields/priotagmenu.vue";

import ColorPicker from "@/components/color-picker.vue";

import KAppLogo from "./components/framework/theme/k-app-logo";
import KAppSidebar from "./components/framework/theme/k-app-sidebar";
import KAppTopbar from "./components/framework/theme/k-app-topbar";
import KApp404 from "./components/framework/theme/k-app-404";
import KNoPermission from "./components/framework/permissions/k-no-permission";
import KSpacing from "./components/framework/components/k-spacing";
import KAvatarEdit from "./components/framework/components/k-avatar-edit";
import KFileupload from "./components/framework/components/k-fileupload";
import VaCard from "./components/vapps/vacard";
import VaBtn from "./components/vapps/vabtn";
import VaDialog from "./components/vapps/vadialog";
import FileUploadField from "./components/vapps/fileuploadfield";
import FilterMenu from "./components/views/meta/FilterMenu";
import SidebarMoreModules from "./components/home/SidebarMoreModules";
import Timelinecard from "./components/timeline/Timelinecard";
import CollapseSection from "@/components/CollapseSection";
import CollapseFolder from "@/components/CollapseFolder";
import TipTapInput from "@/components/TipTapInput";
import BtnIconGroup from "@/components/BtnIconGroup.vue";
import KBtnToggle from "@/components/field-settings/components/k-btn-toggle.vue";
import KIconPicker from "@/components/field-settings/components/k-icon-picker.vue";
import KFieldsRepeater from "@/components/field-settings/components/k-fields-repeater.vue";
import KBignumbersRepeater from "@/components/field-settings/components/k-bignumbers-repeater.vue";
import KChartRepeater from "@/components/field-settings/components/k-chart-repeater.vue";
import KBarcode from "@/components/field-settings/components/k-barcode.vue";
import KQrcode from "@/components/field-settings/components/k-qrcode.vue";

// Register components

app.component("home-sidebar", HomeSidebar);
app.component("admin-sidebar", AdminSidebar);

app.component("dashboard-topbar", DashTopbar);
app.component("assignees", Assignees);
app.component("userstagmenu", Userstagmenu);
app.component("timetrackingmenu", Timetrackingmenu);
app.component("statustag", Statustag);
app.component("statustagmenu", statustagmenu);
app.component("priotag", Priotag);
app.component("priotagmenu", Priotagmenu);

import KFieldGroup from "./components/apps/field-controls/k-field-group.vue";
app.component("k-field-group", KFieldGroup);

app.component("color-picker", ColorPicker);

app.component("k-app-logo", KAppLogo);
app.component("k-app-sidebar", KAppSidebar);
app.component("k-app-topbar", KAppTopbar);
app.component("k-app-404", KApp404);
app.component("k-no-permission", KNoPermission);
app.component("k-spacing", KSpacing);
app.component("k-avatar-edit", KAvatarEdit);
app.component("k-fileupload", KFileupload);
app.component("va-card", VaCard);
app.component("va-btn", VaBtn);
app.component("va-dialog", VaDialog);
app.component("file-upload-field", FileUploadField);
app.component("filter-menu", FilterMenu);
app.component("sidebar-more-modules", SidebarMoreModules);
app.component("timelinecard", Timelinecard);
app.component("collapse-section", CollapseSection);
app.component("collapse-folder", CollapseFolder);
app.component("tip-tap-input", TipTapInput);
app.component("btn-icon-group", BtnIconGroup);
app.component("k-btn-toggle", KBtnToggle);
app.component("k-icon-picker", KIconPicker);
app.component("k-fields-repeater", KFieldsRepeater);
app.component("k-bignumbers-repeater", KBignumbersRepeater);
app.component("k-chart-repeater", KChartRepeater);
app.component("k-barcode", KBarcode);
app.component("k-qrcode", KQrcode);
app.component("VueDatePicker", VueDatePicker);
app.component("draggable", VueDraggableNext);

// UI components
import Icon from "@/components/ui/icon.vue";
import IconPicker from "@/components/ui/icon-picker.vue";
import ListItem from "@/components/ui/list-item.vue";
import Input from "@/components/ui/input.vue";
import Textarea from "@/components/ui/textarea.vue";
import Breadcrumb from "@/components/ui/breadcrumb.vue";
import Wysiwyg from "@/components/ui/wysiwyg.vue";
import Alert from "@/components/ui/alert.vue";

app.component("ui-icon", Icon);
app.component("ui-icon-picker", IconPicker);
app.component("ui-list-item", ListItem);
app.component("ui-input", Input);
app.component("ui-textarea", Textarea);
app.component("ui-breadcrumb", Breadcrumb);
app.component("ui-wysiwyg", Wysiwyg);
app.component("ui-alert", Alert);

// Widgets settings

import Visibility from "@/components/apps/widgets/fields-groups/Visibility.vue";
app.component("visibility", Visibility);

import FilterData from "@/components/apps/widgets/fields-groups/FilterData.vue";
app.component("filter-data", FilterData);

import SortBy from "@/components/apps/widgets/fields-groups/SortBy.vue";
app.component("sort-by", SortBy);

import ActionsRepeater from "@/components/apps/widgets/fields-groups/actions-repeater.vue";
app.component("ui-actions-repeater", ActionsRepeater);

import FormStepperRepeater from "@/components/apps/widgets/fields-groups/form-stepper-repeater.vue";
app.component("form-stepper-repeater", FormStepperRepeater);

import ActionTypes from "@/components/apps/widgets/fields-groups/action-types.vue";
app.component("action-types", ActionTypes);

import FieldTypes from "@/components/apps/widgets/fields-groups/field-types.vue";
app.component("field-types", FieldTypes);

import AllFields from "@/components/apps/widgets/fields-groups/all-fields.vue";
app.component("all-fields", AllFields);

import InAppFilter from "@/components/apps/widgets/components/InAppFilter.vue";
app.component("in-app-filter", InAppFilter);

import inAppFilterSettings from "@/components/apps/widgets/fields-groups/inAppFilterSettings.vue";
app.component("in-app-filter-settings", inAppFilterSettings);

import ActionButtons from "@/components/apps/widgets/components/ActionButtons.vue";
app.component("action-buttons", ActionButtons);

import ActionLists from "@/components/apps/widgets/components/ActionLists.vue";
app.component("action-lists", ActionLists);

import SettingsTabs from "@/components/apps/widgets/components/SettingsTabs.vue";
app.component("settings-tabs", SettingsTabs);

import CollectionSelect from "@/components/apps/widgets/components/CollectionSelect.vue";
app.component("collections-select", CollectionSelect);

import ImageSelect from "@/components/apps/widgets/components/ImageSelect.vue";
app.component("image-select", ImageSelect);

import FieldsSelect from "@/components/apps/widgets/components/FieldsSelect.vue";
app.component("fields-select", FieldsSelect);

import CollectionsTopbar from "@/components/apps/widgets/components/CollectionsTopbar.vue";
app.component("collections-topbar", CollectionsTopbar);

// Use Pinia and other plugins
app.use(pinia);
const mainStore = useMainStore();
app.provide("mainStore", mainStore);
app.use(router);
app.use(vuetify);
app.use(Toast, toastOptions);
app.use(VueFlow);

// Finally, mount the app
app.mount("#app");
